// WP-1.7 (EX-2, ST-2): category tree queries/mutations over the engine-backed API.
import {
  categoryListResponseSchema,
  categoryReassignResponseSchema,
  categoryResponseSchema,
  type CategoryCreateRequest,
  type CategoryEditRequest,
  type CategoryReassignRequest,
  type CategoryReassignResponse,
  type CategoryResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';
import { TRANSACTIONS_KEY } from './transactions';

export const CATEGORIES_KEY = ['categories'] as const;

export function useCategories(
  filters: { kind?: 'expense' | 'income'; includeArchived?: boolean } = {},
): UseQueryResult<CategoryResponse[]> {
  const params = new URLSearchParams();
  if (filters.kind !== undefined) params.set('kind', filters.kind);
  if (filters.includeArchived === true) params.set('includeArchived', 'true');
  const qs = params.toString();
  return useQuery({
    queryKey: [...CATEGORIES_KEY, filters],
    queryFn: async () => {
      const body = categoryListResponseSchema.parse(
        await api.get(`/api/v1/categories${qs === '' ? '' : `?${qs}`}`),
      );
      return body.categories;
    },
  });
}

function useInvalidateCategories(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: CATEGORIES_KEY });
  };
}

export function useCreateCategory(): UseMutationResult<
  CategoryResponse,
  Error,
  CategoryCreateRequest
> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (input) =>
      categoryResponseSchema.parse(await api.post('/api/v1/categories', input)),
    onSuccess: invalidate,
  });
}

export function useEditCategory(
  id: string,
): UseMutationResult<CategoryResponse, Error, CategoryEditRequest> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (input) =>
      categoryResponseSchema.parse(await api.patch(`/api/v1/categories/${id}`, input)),
    onSuccess: invalidate,
  });
}

/** EX-2 "re-orderable": `ids` is the full new order of one sibling group. */
export function useReorderCategories(): UseMutationResult<CategoryResponse[], Error, string[]> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (ids) => {
      const body = categoryListResponseSchema.parse(
        await api.post('/api/v1/categories/reorder', { ids }),
      );
      return body.categories;
    },
    onSuccess: invalidate,
  });
}

/** EX-2 "bulk re-categorization" — also invalidates transactions (their category changed). */
export function useReassignCategory(): UseMutationResult<
  CategoryReassignResponse,
  Error,
  CategoryReassignRequest
> {
  const invalidateCategories = useInvalidateCategories();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      categoryReassignResponseSchema.parse(await api.post('/api/v1/categories/reassign', input)),
    onSuccess: async () => {
      await Promise.all([
        invalidateCategories(),
        queryClient.invalidateQueries({ queryKey: TRANSACTIONS_KEY }),
      ]);
    },
  });
}

/** §16.11 seed loader. */
export function useSeedDefaultCategories(): UseMutationResult<CategoryResponse[], Error, void> {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async () => {
      const body = categoryListResponseSchema.parse(
        await api.post('/api/v1/categories/seed-defaults'),
      );
      return body.categories;
    },
    onSuccess: invalidate,
  });
}
